import {
  Injectable,
  computed,
  signal,
  inject,
  effect,
  WritableSignal, Signal
} from '@angular/core';
import {AiSettingsInterface, DEFAULT_TOOLS_AI_SETTINGS} from '@llm/interfaces/settings-ai';
import {MicrophoneService} from '@serv/media_access/api_web_access_microphone.service';
/**
 * @Injectable
 * @description
 * Serviço singleton que atua como o "store" central de estado reativo para o módulo de IA.
 * Utiliza Angular Signals para gerenciar o estado de forma eficiente e previsível.
 *
 * @Responsabilidades
 * - Manter o estado de todas as operações da IA (gravação, processamento, etc.).
 * - Encapsular a lógica de modificação do estado, expondo métodos seguros (setters/toggles).
 * - Fornecer acesso de apenas leitura ou controlado ao estado para o resto da aplicação.
 *
 * @param providedIn 'root' - Garante que este serviço seja um singleton em toda a aplicação.
 */
@Injectable({
  providedIn: 'root'
})
export class llmStoreService {
  //Injectbales
  private _accessMicSvc = inject(MicrophoneService)

  // --- Sinais Privados ---
  /**
   * @private
   * @description
   * Sinais privados que contêm o estado real. O prefixo `_` e o modificador `private`
   * garantem o encapsulamento. A modificação do estado só deve ocorrer através dos
   * métodos públicos deste serviço.
   */

  /** @description Sinal que indica se o sistema está ativamente a gravar o áudio do utilizador. */
  private _isRecording = signal<boolean>(false);
  private _ro_create_isRecording = signal<boolean>(false);
  /** @description Sinal que reflete a intenção do utilizador de interagir por voz. Controla a ativação automática do microfone. */
  public readonly isInteractionVoiceIntentStatus = computed(() => this.settingsToolsAi().voiceInteractionEnabled);

  public isExpandedForComplexContent = signal<boolean>(false)
  /** @description Sinal para controlar o estado de pausa da gravação. */
  private _isPauseRecording = signal<boolean>(false);
  /** @description Sinal que indica se o sistema está em modo de escuta ativa para comandos de voz. */
  public readonly isListening = computed(() => this._accessMicSvc.isRecognizing() && this._accessMicSvc.isConsumerActive('COMMAND_DETECTOR'));

  public action_instant=signal<string>('')
  /** @description Sinal que indica se o sistema está a reproduzir áudio (falando). */
  private _isSpeaking = signal<boolean>(false);
  /** @description Sinal que indica se um ficheiro de áudio está a ser processado (transcrito). */
  private _isProcessingAudio = signal<boolean>(false);
  /** @description Sinal que indica se o texto está a ser processado pelo modelo de linguagem. */
  private _isProcessingText = signal<boolean>(false);
  /** @description Sinal que indica se o utilizador está a digitar no campo de texto do chat. */
  private _isUserTypingText = signal<boolean>(false);
  /** @description Sinal para controlar a visibilidade da interface do chat. */
  private _isShowChat = signal<boolean>(false);
  /** @description Sinal que se torna `true` quando uma nova mensagem do sistema é gerada, para acionar animações ou scrolls. */
  private _isNewMessage = signal<boolean>(false);
  /** @description Contador para renderizações do chat. `0` significa que nunca foi aberto. */
  private _isFirstRenderedChat = signal<number>(0);
  /** @description Sinal que armazena se o utilizador concedeu permissão de acesso ao microfone. */
  public readonly isMicrophoneAccessGranted = computed(() => this._accessMicSvc.isAccessMicrophone());
  private _permissionMicStatus = signal<MicrophonePermissionStatus>('unknown');
  public readonly isNotMicSupported = computed(() => this._permissionMicStatus() === 'not-supported');
  /** @description Sinal que armazena o comando de voz "nitro" atualmente ativo. */
  private _commandActivedByNitro = signal<string>('');
  /** @description Buffer que armazena uma lista de comandos "nitro" ativos. */
  private _bufferCommandActivedByNitro = signal<string[]>([]);
  /** @description Acumula a transcrição de uma sessão de áudio contínua. */
  private _transcriptionSession = signal<string>('');
  /** @description Armazena o estado atual da gravação (inativo, a gravar, em pausa). */
  private _recordingStatus = signal<RecordingStatus>(RecordingStatus.INACTIVE);
  /** @description Sinal que armazena o agente de IA atualmente em uso. */
  private _agentActually = signal<string>('');
  /** @description Sinal para controlar o estado da extração de agentes: 0 (inicial), 1 (em processamento), -1 (processado). */
  private _agentExtracted = signal<-1 | 0 | 1>(0);

  /** @description Sinal privado e editável que contém o estado atual das configurações da IA. */
  private settingsToolsAiSignal: WritableSignal<AiSettingsInterface>;
  /** @description Sinal público e de apenas leitura. Os componentes irão consumi-lo para obter as configurações. */
  public readonly settingsToolsAi: Signal<AiSettingsInterface>;

  public _current_apikey = signal<number>(0);

  /**
   * @constructor
   * @description Inicializa o serviço. A principal responsabilidade aqui é configurar
   * o sinal de configurações (`settingsToolsAiSignal`) e expor a sua versão de apenas leitura.
   */
  constructor() {
    // Inicializa o sinal privado com os valores padrão.
    this.settingsToolsAiSignal = signal(DEFAULT_TOOLS_AI_SETTINGS);
    // Cria e atribui a vista de apenas leitura à propriedade pública.
    this.settingsToolsAi = this.settingsToolsAiSignal.asReadonly();
  }


  // --- Getters Públicos ---
  /**
   * @description
   * Getters públicos que expõem os sinais privados.
   * Isto permite que outras partes da aplicação leiam o estado de forma reativa,
   * mas impede que modifiquem o estado diretamente, forçando o uso dos setters.
   */
  get isRecording(): WritableSignal<boolean> {
    return this._isRecording;
  }

  get ro_create_isRecording(): WritableSignal<boolean> {
    return this._ro_create_isRecording;
  }

  get getSettingsToolsAiSignal(): WritableSignal<AiSettingsInterface> {
    return this.settingsToolsAiSignal;
  }


  get isSpeaking(): WritableSignal<boolean> {
    return this._isSpeaking;
  }

  get isProcessingAudio(): WritableSignal<boolean> {
    return this._isProcessingAudio;
  }

  get isProcessingText(): WritableSignal<boolean> {
    return this._isProcessingText;
  }

  get isUserTypingText(): WritableSignal<boolean> {
    return this._isUserTypingText;
  }

  get isShowChat(): WritableSignal<boolean> {
    return this._isShowChat;
  }

  get isNewMessage(): WritableSignal<boolean> {
    return this._isNewMessage;
  }

  get isFirstRenderedChat(): WritableSignal<number> {
    return this._isFirstRenderedChat;
  }

  get isPauseRecording(): WritableSignal<boolean> {
    return this._isPauseRecording;
  }

  get commandActivedByNitro(): WritableSignal<string> {
    return this._commandActivedByNitro;
  }

  get bufferCommandActivedByNitro(): WritableSignal<string[]> {
    return this._bufferCommandActivedByNitro;
  }

  get transcriptionSession(): WritableSignal<string> {
    return this._transcriptionSession;
  }

  get recordingStatus(): WritableSignal<RecordingStatus> {
    return this._recordingStatus;
  }

  get agentExtracted(): WritableSignal<-1 | 0 | 1> {
    return this._agentExtracted;
  }


  // --- Setters Públicos ---
  /**
   * @description
   * Métodos públicos para modificar o estado. Esta é a forma controlada e recomendada
   * para alterar o valor dos sinais. Utilizam `.set()` ou `.update()` para garantir
   * que as mudanças sejam propagadas reativamente.
   */

  /** @description Define o estado de gravação do microfone. */
  public setRecording(status: boolean): void {
    this._isRecording.set(status);
  }

  public setRoCreateRecording(status: boolean): void {
    this._ro_create_isRecording.set(status);
  }

  public setPermissionMicStatus(status: MicrophonePermissionStatus): void {
    this._permissionMicStatus.set(status);
  }

  /** @description Define o objeto completo de configurações da IA. */
  public setSettingsToolsAi(status: AiSettingsInterface): void {
    this.settingsToolsAiSignal.set(status);
  }

  /** @description Define se o sistema está a falar. */
  public setSpeaking(status: boolean): void {
    this._isSpeaking.set(status);
  }

  /** @description Define se o áudio está a ser processado. */
  public setProcessingAudio(status: boolean): void {
    this._isProcessingAudio.set(status);
  }

  /** @description Define se o texto está a ser processado. */
  public setProcessingText(status: boolean): void {
    this._isProcessingText.set(status);
  }

  /** @description Define se o utilizador está a digitar. */
  public setUserTypingText(status: boolean): void {
    this._isUserTypingText.set(status);
  }

  /** @description Define a visibilidade do chat. */
  public setShowChat(status: boolean): void {
    this._isShowChat.set(status);
  }

  /** @description Define o estado de nova mensagem. */
  public setNewMessage(status: boolean): void {
    this._isNewMessage.set(status);
  }

  /** @description Define o comando "nitro" ativo. */
  public setCommandActivedByNitro(status: string): void {
    this._commandActivedByNitro.set(status);
  }

  /** @description Adiciona uma transcrição à sessão atual. */
  public setTranscriptionSession(transcription: string): void {
    this._transcriptionSession.update(current => current + transcription);
  }

  /** @description Adiciona um comando ao buffer de comandos "nitro". */
  public setBufferCommandActivedByNitroAddCommand(status: string): void {
    this._bufferCommandActivedByNitro.update(current => [...current, status]);
  }

  /** @description Remove um comando específico do buffer de comandos "nitro". */
  public setBufferCommandActivedByNitroRemoveCommand(status: string): void {
    this._bufferCommandActivedByNitro.update(current => current.filter(item => item !== status));
  }

  /** @description Define o estado da gravação (inativo, a gravar, etc.). */
  public setRecordingStatus(status: RecordingStatus): void {
    this._recordingStatus.set(status);
  }

  /** @description Define o estado da extração de agentes. */
  public setAgentExtracted(status: -1 | 0 | 1): void {
    this._agentExtracted.set(status);
  }

  // --- Toggles Públicos ---
  /**
   * @description
   * Métodos de conveniência para alternar valores booleanos.
   */

  /** @description Alterna o estado de gravação. */
  public toggleRecording(): void {
    this._isRecording.update(v => !v);
  }


  public toggleRoCreateRecording(): void {
    this._ro_create_isRecording.update(v => !v);
  }

  /** @description Alterna o estado de fala. */
  public toggleSpeaking(): void {
    this._isSpeaking.update(v => !v);
  }

  /** @description Alterna o estado de processamento de áudio. */
  public toggleProcessingAudio(): void {
    this._isProcessingAudio.update(v => !v);
  }

  /** @description Alterna o estado de processamento de texto. */
  public toggleProcessingText(): void {
    this._isProcessingText.update(v => !v);
  }

  /** @description Alterna o estado de digitação do utilizador. */
  public toggleUserTypingText(): void {
    this._isUserTypingText.update(v => !v);
  }

  /** @description Alterna a visibilidade do chat e incrementa o contador de renderização. */
  public toggleShowChat(): void {
    this._isShowChat.update(v => !v);
    this._isFirstRenderedChat.update(v => v + 1);
  }


  /** @description Alterna o estado de nova mensagem. */
  public toggleNewMessage(): void {
    this._isNewMessage.update(v => !v);
  }

  /** @description Incrementa o contador de renderização do chat. */
  public toggleFirstRenderedChat(): void {
    this._isFirstRenderedChat.update(v => v + 1);
  }


  // --- Setters de Propriedade (Uso Avanzado pues elimina las referencias) ---
  /**
   * @deprecated
   * @description
   * Setters que reatribuem a instância completa do `WritableSignal`.
   * **AVISO:** O seu uso é desaconselhado, pois quebra
   * referências reativas em componentes consumidores. Porem prefira sempre os métodos
   * `set...()` ou `toggle...()` para modificar o *valor* do sinal.
   */
  set isRecording(value: WritableSignal<boolean>) {
    this._isRecording = value;
  }

  set ro_create_isRecording(value: WritableSignal<boolean>) {
    this._ro_create_isRecording = value;
  }

  set isSpeaking(value: WritableSignal<boolean>) {
    this._isSpeaking = value;
  }

  set isProcessingAudio(value: WritableSignal<boolean>) {
    this._isProcessingAudio = value;
  }

  set isProcessingText(value: WritableSignal<boolean>) {
    this._isProcessingText = value;
  }

  set isUserTypingText(value: WritableSignal<boolean>) {
    this._isUserTypingText = value;
  }

  set isShowChat(value: WritableSignal<boolean>) {
    this._isShowChat = value;
  }

  set isNewMessage(value: WritableSignal<boolean>) {
    this._isNewMessage = value;
  }

  set isFirstRenderedChat(value: WritableSignal<number>) {
    this._isFirstRenderedChat = value;
  }

  set CommandActivedByNitro(value: WritableSignal<string>) {
    this._commandActivedByNitro = value;
  }

  set BufferCommandActivedByNitro(value: WritableSignal<string[]>) {
    this._bufferCommandActivedByNitro = value;
  }

  set TranscriptionSession(value: WritableSignal<string>) {
    this._transcriptionSession = value;
  }

  set RecordingStatus(value: WritableSignal<RecordingStatus>) {
    this._recordingStatus = value;
  }

  set agentExtracted(value: WritableSignal<-1 | 0 | 1>) {
    this._agentExtracted = value;
  }
}

/**
 * @enum RecordingStatus
 * @description Enum que define os possíveis estados do processo de gravação de áudio.
 */
export enum RecordingStatus {
  /** @description Não há nenhuma gravação ativa. */
  INACTIVE = 'inactive',
  /** @description O microfone está ativamente a capturar áudio. */
  RECORDING = 'recording',
  /** @description A gravação foi temporariamente pausada pelo utilizador. */
  PAUSED = 'paused'
}

/**
 * @type MicrophonePermissionStatus
 * @description Define os possíveis estados de permissão para o acesso ao microfone,
 * refletindo os estados da API de Permissões do navegador.
 */
export type MicrophonePermissionStatus =
  'unknown' |         // Estado inicial desconocido
  'granted' |         // Permiso concedido
  'denied' |          // Permiso denegado por el usuario
  'prompt' |          // El navegador preguntará si se solicita
  'not-supported' |   // API no soportada en el navegador
  'no-device' |       // No se encontró un micrófono
  'error';            // Otro error inesperado

